import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminAdManagement = () => {
  const [adData, setAdData] = useState({ url: '', count: 0, lastUpdated: null });
  const [newUrl, setNewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchAdData();
  }, []);

  const getAuthHeaders = () => {
    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    return {
      'admin-email': adminUser.email
    };
  };

  const fetchAdData = async () => {
    try {
      const response = await axios.get('/api/admin/ads/redirect', {
        headers: getAuthHeaders()
      });
      setAdData(response.data);
      setNewUrl(response.data.url || '');
    } catch (error) {
      console.error('Error fetching ad data:', error);
      alert('Failed to load ad data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUrl = async (e) => {
    e.preventDefault();
    if (!newUrl.trim()) {
      alert('Please enter a valid URL');
      return;
    }

    setUpdating(true);
    try {
      const response = await axios.post('/api/admin/ads/redirect', { url: newUrl.trim() }, {
        headers: getAuthHeaders()
      });
      setAdData(response.data.ads);
      alert('Redirect URL updated successfully!');
    } catch (error) {
      console.error('Error updating URL:', error);
      alert('Failed to update redirect URL');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Calculate traffic analytics
  const getTrafficAnalytics = () => {
    if (!adData.traffic || adData.traffic.length === 0) {
      return {
        totalVisitors: 0,
        uniqueVisitors: 0,
        topSources: [],
        topReferrers: [],
        deviceBreakdown: {},
        recentTraffic: []
      };
    }

    const traffic = adData.traffic;
    const uniqueVisitors = new Set(traffic.map(t => t.visitorId)).size;

    // Top sources (UTM)
    const sources = {};
    traffic.forEach(t => {
      if (t.utm_source) {
        sources[t.utm_source] = (sources[t.utm_source] || 0) + 1;
      }
    });
    const topSources = Object.entries(sources)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([source, count]) => ({ source, count }));

    // Top referrers
    const referrers = {};
    traffic.forEach(t => {
      const referrer = t.referrer === 'direct' ? 'Direct' : new URL(t.referrer || 'https://unknown').hostname;
      referrers[referrer] = (referrers[referrer] || 0) + 1;
    });
    const topReferrers = Object.entries(referrers)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([referrer, count]) => ({ referrer, count }));

    // Device breakdown
    const devices = {};
    traffic.forEach(t => {
      const ua = t.userAgent?.toLowerCase() || '';
      let device = 'Desktop';
      if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
        device = 'Mobile';
      } else if (ua.includes('tablet') || ua.includes('ipad')) {
        device = 'Tablet';
      }
      devices[device] = (devices[device] || 0) + 1;
    });

    // Recent traffic (last 10)
    const recentTraffic = traffic
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 10);

    return {
      totalVisitors: traffic.length,
      uniqueVisitors,
      topSources,
      topReferrers,
      deviceBreakdown: devices,
      recentTraffic
    };
  };

  const analytics = getTrafficAnalytics();

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Ad Redirect Management</h1>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">Current Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-600 uppercase">Total Redirects</h3>
            <p className="text-2xl font-bold text-blue-900">{adData.count}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-600 uppercase">Total Visitors</h3>
            <p className="text-2xl font-bold text-green-900">{analytics.totalVisitors}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-600 uppercase">Unique Visitors</h3>
            <p className="text-2xl font-bold text-purple-900">{analytics.uniqueVisitors}</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-600 uppercase">Redirect URL</h3>
            <p className="text-sm font-mono text-yellow-900 break-all">
              {adData.url || 'Not set'}
            </p>
          </div>
          <div className="bg-indigo-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-indigo-600 uppercase">Last Updated</h3>
            <p className="text-sm text-indigo-900">{formatDate(adData.lastUpdated)}</p>
          </div>
        </div>
      </div>

      {/* Traffic Analytics Section */}
      {analytics.totalVisitors > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Top Sources */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Top Traffic Sources</h3>
            {analytics.topSources.length > 0 ? (
              <div className="space-y-2">
                {analytics.topSources.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-700">{item.source}</span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No UTM source data available</p>
            )}
          </div>

          {/* Top Referrers */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Top Referrers</h3>
            {analytics.topReferrers.length > 0 ? (
              <div className="space-y-2">
                {analytics.topReferrers.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-700 truncate" title={item.referrer}>
                      {item.referrer}
                    </span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No referrer data available</p>
            )}
          </div>

          {/* Device Breakdown */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Device Breakdown</h3>
            {Object.keys(analytics.deviceBreakdown).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(analytics.deviceBreakdown).map(([device, count]) => (
                  <div key={device} className="flex justify-between items-center">
                    <span className="text-gray-700">{device}</span>
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No device data available</p>
            )}
          </div>

          {/* Recent Traffic */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Recent Traffic</h3>
            {analytics.recentTraffic.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {analytics.recentTraffic.map((traffic, index) => (
                  <div key={index} className="border-b border-gray-200 pb-2 last:border-b-0">
                    <div className="text-sm text-gray-600">
                      {formatDate(traffic.timestamp)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {traffic.utm_source ? `${traffic.utm_source} ? ` : ''}
                      {traffic.referrer === 'direct' ? 'Direct' : new URL(traffic.referrer || 'https://unknown').hostname}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No recent traffic data</p>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">Update Redirect URL</h2>
        <form onSubmit={handleUpdateUrl} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Redirect URL
            </label>
            <input
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              Enter the URL where visitors should be redirected when they visit /ad
            </p>
          </div>
          <button
            type="submit"
            disabled={updating}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {updating ? 'Updating...' : 'Update URL'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminAdManagement;
